import { FC, useMemo } from 'react';

// Third-party
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import moment from 'moment';

// Components
import startCase from 'lodash/startCase';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';

// Hooks
import { useTranslation } from 'react-i18next';
import { getCalendarDates } from '@mgm/common/features/schedules';
import { getTimezone, toFixedString, formatName } from '@mgm/common/utils';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { useSchedules } from '../../Schedule/hooks';

// Redux-toolkit
import { getCurrencyById } from '../../../../store/common/selectors';

// Types
import { TScheduleShift } from '../../../../types';
import { TScheduleAvatar } from './type';
import { useSchedulePermissions } from '../hooks';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { getScheduleList } from '../../../../store/schedules/selectors';

const ScheduleAvatar: FC<TScheduleAvatar> = ({ member, selectedDate }) => {
  const { getPersonShifts } = useSchedules();
  const { t } = useTranslation('team');
  const getCurrency = useAppSelector(getCurrencyById);
  const { schedulePermissions } = useSchedulePermissions();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const scheduleList = useAppSelector(getScheduleList);

  const totalData = useMemo(() => {
    const weekDates = getCalendarDates(selectedDate);
    let totalHoursInSeconds = 0;

    const userShifts = weekDates.reduce((acc, date) => {
      acc.push(...getPersonShifts(member.email, moment.tz(date, getTimezone())));
      return acc;
    }, [] as Array<TScheduleShift>);

    userShifts.forEach((shift) => {
      totalHoursInSeconds += shift.timeInSeconds;
    });

    const totalHours = totalHoursInSeconds / 3600; // Convert to hours
    const totalHoursFormatted = `${Math.floor(totalHoursInSeconds / 3600)}:${moment.utc(totalHoursInSeconds * 1000).format('mm')}h`;

    const paymentAmount = Number(member?.payment?.paymentAmount || 0); // Explicit type conversion
    const paymentCurrencyId = member?.payment?.paymentCurrencyId || 2;
    const paymentType = member?.payment?.paymentType || 'Hourly Wage';
    const totalAmount = toFixedString(paymentAmount * totalHours, 2); // Ensure paymentAmount and totalHours are numbers
    const memberCurrencyMark = getCurrency(paymentCurrencyId).mark;

    return {
      totalHours: totalHoursFormatted,
      totalAmount: paymentType === 'Salary' ? `${memberCurrencyMark}${paymentAmount}/m` : `${memberCurrencyMark}${totalAmount}`,
    };
  }, [selectedDate, member, scheduleList, getPersonShifts, getCurrency]);

  return (
    <ListItem
      sx={{
        px: 1,
      }}
      alignItems={schedulePermissions.isAllowViewSalaryData ? 'flex-start' : 'center'}
    >
      <ListItemAvatar>
        <MemoizedBaseAvatar
          statusTooltipText={t('pendingRegistration')}
          status={member.status}
          avatarName={member.name}
          avatarFile={member.avatarFile}
        />
      </ListItemAvatar>
      <ListItemText
        primaryTypographyProps={{
          variant: 'body2',
        }}
        primary={startCase(formatName(member.name))}
        {...(schedulePermissions.isAllowViewSalaryData || member.email === personnel.email) && {
          secondary: (
            `${totalData.totalHours} - ${totalData.totalAmount}`
          ),
        }}
        secondaryTypographyProps={{
          variant: 'caption',
        }}
      />
    </ListItem>
  );
};

export { ScheduleAvatar };
